import { Chip, Paper, Table, Typography } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { QUERY_PROGRESS_PENDING } from "core/consts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { getKey } from "core/model/utils/strings";
import { FileHISStateful, QueryProgress } from "core/types";
import { LockSecurePin } from "ds_legacy/components/Icons/SecurePin";
import SpinnerPage from "ds_legacy/components/Spinner";
import Tooltip from "ds_legacy/components/Tooltip";
import { GREY_600, TEXT_DISABLED_COLOR } from "ds_legacy/materials/colors";
import { margin, padding } from "ds_legacy/materials/metrics";
import {
  FONT_SIZE_12,
  FONT_SIZE_14,
  FONT_SIZE_18,
  LINE_HEIGHT_18,
} from "ds_legacy/materials/typography";
import { EncryptedFileMessage } from "dsl/organisms/FileTables";
import { Dispatch, MouseEvent, SetStateAction } from "react";
import styled from "styled-components";

const StyledTableCell = styled(TableCell)<{ disabled: boolean | undefined }>`
  border-bottom: none !important;
  font-size: ${FONT_SIZE_14};
  color: ${({ disabled }) =>
    disabled ? `${TEXT_DISABLED_COLOR} !important` : undefined};
`;

export function FilesTableHIS({
  data,
  description,
  filesSelected,
  queryProgress,
  setFilesSelected,
  title,
}: {
  data: FileHISStateful[] | null;
  description: string;
  filesSelected: FileHISStateful[] | null;
  queryProgress: QueryProgress;
  setFilesSelected: Dispatch<SetStateAction<FileHISStateful[]>> | null;
  title: string;
}) {
  const getOntology = useGetOntology();

  const handleClick = (
    event: MouseEvent<unknown>,
    document: FileHISStateful,
  ) => {
    if (!filesSelected || !setFilesSelected) return;

    const selectedIndex = filesSelected.findIndex(
      (item) => item.document_id === document.document_id,
    );
    let newSelected: FileHISStateful[] = [];

    if (selectedIndex === -1) {
      newSelected = [...filesSelected, document];
    } else {
      newSelected = [
        ...filesSelected.slice(0, selectedIndex),
        ...filesSelected.slice(selectedIndex + 1),
      ];
    }

    setFilesSelected(newSelected);
  };

  const isSelected = (document_id: FileHISStateful["document_id"]) => {
    if (!filesSelected) return false;
    return filesSelected.some(
      (document) => document.document_id === document_id,
    );
  };

  return (
    <Paper elevation={0} sx={{ mb: 2, width: "100%" }}>
      <div style={{ padding: padding(2, 0) }}>
        <Typography
          component="div"
          id="tableTitle"
          sx={{
            flex: "1 1 100%",
            fontSize: FONT_SIZE_18,
            fontWeight: 700,
            padding: padding(0, 2),
          }}
          variant="h6"
        >
          {title} ({data?.length || 0})
        </Typography>
        <Typography
          component="div"
          sx={{
            color: GREY_600,
            fontSize: FONT_SIZE_12,
            lineHeight: LINE_HEIGHT_18,
            padding: padding(0, 2),
          }}
        >
          {description}
          <EncryptedFileMessage />
        </Typography>
      </div>
      {queryProgress === QUERY_PROGRESS_PENDING ? (
        <SpinnerPage delay={0} id="file-table-his" size="small" />
      ) : (
        <Table size="small" sx={{ width: "100%" }}>
          <TableBody>
            {data?.map((file, index) => {
              const {
                document_category_number,
                document_file_name,
                document_id,
                unavailable_reason,
              } = file;
              const disabled = !!unavailable_reason;
              const isItemSelected = isSelected(document_id);
              const labelId = `file-table-checkbox-${index}`;

              return (
                <TableRow
                  key={getKey(document_id, document_file_name)}
                  aria-checked={isItemSelected}
                  data-testid={
                    disabled
                      ? `unavailable-files-row-${document_id}`
                      : `available-files-row-${document_id}`
                  }
                  hover={!disabled}
                  role="checkbox"
                  selected={isItemSelected}
                  sx={{ cursor: disabled ? "auto" : "pointer" }}
                  tabIndex={-1}
                  onClick={(event) => !disabled && handleClick(event, file)}
                >
                  {!disabled && (
                    <StyledTableCell disabled={disabled} padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        color="primary"
                        disabled={disabled}
                        inputProps={{ "aria-labelledby": labelId }}
                        size="small"
                      />
                    </StyledTableCell>
                  )}
                  <StyledTableCell
                    aria-disabled={disabled}
                    component="th"
                    disabled={disabled}
                    id={labelId}
                    scope="row"
                  >
                    <Tooltip
                      title={getOntology({
                        key: document_category_number,
                        type: ONTOLOGIES.FILE_CATEGORY,
                      })}
                    >
                      <Chip
                        label={getOntology({
                          key: document_category_number,
                          type: ONTOLOGIES.FILE_CATEGORY,
                          useShortValues: true,
                        })}
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  </StyledTableCell>
                  <StyledTableCell
                    align="left"
                    disabled={false}
                    style={{ width: "100%" }}
                  >
                    <span
                      style={{
                        alignItems: "center",
                        color: "inherit",
                        display: "flex",
                      }}
                    >
                      {document_file_name}
                      <LockSecurePin
                        key="encrypted-badge"
                        style={{
                          color: GREY_600,
                          fontSize: FONT_SIZE_14,
                          margin: margin(0, 0, 0, 0.5),
                        }}
                        tooltip=""
                      />
                    </span>
                  </StyledTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

export default FilesTableHIS;
